import { gql } from 'graphql-tag';

/**
 * VideoAsk GraphQL Operations
 *
 * GraphQL queries and mutations for VideoAsk operations via Nexus.
 * Used by the m-video-questions widget for reattach and delete operations.
 *
 * @see Nexus: /services/videoask/videoask.resolver.ts
 */

// ============================================================
// Mutations
// ============================================================

/**
 * Reattach a VideoAsk response that may have been missed by the webhook.
 *
 * @requires Authentication - User must be logged in
 * @description Queries the VideoAsk API to find if the user has completed the form,
 *              then stores the response in the Student document.
 * @param input - ReattachVideoAskInput with userId, programId, adkName, optional formId
 * @returns VideoAskOperationResult with success status and message
 */
export const REATTACH_VIDEOASK_RESPONSE_MUTATION = gql`
  mutation ReAttachVideoaskResponse($input: ReattachVideoAskInput!) {
    reAttachVideoaskResponse(input: $input) {
      success
      message
    }
  }
`;

/**
 * Delete a VideoAsk response (for redo functionality).
 *
 * @requires Authentication - User must be logged in
 * @description Removes the VideoAsk response from the Student document,
 *              allowing the user to record a new response.
 * @param input - DeleteVideoAskInput with userType, programId, publicId, adkName, keyName
 * @returns VideoAskOperationResult with success status and message
 */
export const DELETE_VIDEOASK_RESPONSE_MUTATION = gql`
  mutation DeleteVideoAskResponse($input: DeleteVideoAskInput!) {
    deleteVideoAskResponse(input: $input) {
      success
      message
    }
  }
`;

/**
 * Get the status of a VideoAsk response for a student.
 *
 * @requires Authentication - User must be logged in
 * @description Checks if a student has a VideoAsk response for the specified ADK.
 * @param input - GetVideoAskStatusInput with studentId and adkName
 * @returns VideoAskStatusResult with hasResponse, completedAt, shareUrl
 */
export const GET_VIDEOASK_RESPONSE_STATUS_QUERY = gql`
  query GetVideoAskResponseStatus($input: GetVideoAskStatusInput!) {
    getVideoAskResponseStatus(input: $input) {
      hasResponse
      completedAt
      shareUrl
    }
  }
`;

/**
 * Refresh transcription data from VideoAsk API.
 *
 * @requires Authentication - User must be logged in
 * @description When transcriptions are stuck in "processing" state,
 *              this fetches fresh data from VideoAsk API and updates stored transcriptions.
 * @param studentId - The student document ID
 * @param programId - The program ID
 * @param adkName - The ADK name (e.g., "Reflect")
 * @returns VideoAskOperationResult with success status and message
 */
export const REFRESH_VIDEOASK_TRANSCRIPTION_MUTATION = gql`
  mutation RefreshVideoAskTranscription(
    $studentId: String!
    $programId: String!
    $adkName: String!
  ) {
    refreshVideoAskTranscription(studentId: $studentId, programId: $programId, adkName: $adkName) {
      success
      message
    }
  }
`;

// ============================================================
// TypeScript Types
// ============================================================

/**
 * Input for reattaching a VideoAsk response
 */
export interface ReattachVideoAskInput {
  userId: string;
  programId: string;
  adkName: string;
  formId?: string;
}

/**
 * Input for deleting a VideoAsk response
 */
export interface DeleteVideoAskInput {
  userType: string;
  programId: string;
  publicId?: string;
  adkName: string;
  keyName: string;
  // STUDENT_INSTANCES: optional loaded-instance id so the backend can scope the delete
  // to the exact Student instance. Hand-authored mirror — the backend adds this field
  // to DeleteVideoAskInput in parallel (regenerate codegen once BE ships).
  studentId?: string;
}

/**
 * Input for getting VideoAsk response status
 */
export interface GetVideoAskStatusInput {
  studentId: string;
  adkName: string;
}

/**
 * Result from VideoAsk operations
 */
export interface VideoAskOperationResult {
  success: boolean;
  message?: string;
}

/**
 * Result from VideoAsk status query
 */
export interface VideoAskStatusResult {
  hasResponse: boolean;
  completedAt?: string;
  shareUrl?: string;
}
